import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Permission } from "../roles/types/permissions";
import { buildGroupedByApp, isSystemApp, CrudVerb, ModelGroup } from "../roles/permissionUtils";
import { ApiApplicationGrant } from "./types";

export type VisibleApp = [string, ModelGroup[]];

/**
 * Adapts the Roles app/model permission grouping
 * (permissionUtils.buildGroupedByApp) to drive a per-model CRUD grant picker
 * for API Applications. `/api/authentication/permissions/` is used purely to
 * enumerate app/model pairs - grants here are booleans keyed by
 * "app_label.model", not Django Permission ids.
 */
export function useApiApplicationGrantsPicker(
  allPermissions: Permission[],
  grants: Record<string, ApiApplicationGrant>,
  setGrants: React.Dispatch<React.SetStateAction<Record<string, ApiApplicationGrant>>>,
) {
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
  const [showSystemApps, setShowSystemApps] = useState(false);
  const [showEventModels, setShowEventModels] = useState(false);

  const groupedByApp = useMemo(() => buildGroupedByApp(allPermissions), [allPermissions]);

  const visibleApps = useMemo<VisibleApp[]>(() => {
    const term = deferredSearch.trim().toLowerCase();
    return Object.entries(groupedByApp)
      .filter(([app]) => showSystemApps || !isSystemApp(app))
      .map<VisibleApp>(([app, models]) => [
        app,
        models.filter((m) => showEventModels || !m.isEventTwin),
      ])
      .filter(([app, models]) => {
        if (models.length === 0) return false;
        if (!term) return true;
        return (
          app.toLowerCase().includes(term) ||
          models.some((m) => m.model.toLowerCase().includes(term))
        );
      });
  }, [groupedByApp, showSystemApps, showEventModels, deferredSearch]);

  const toggleApp = useCallback((app: string) => {
    setExpandedApps((prev) => ({ ...prev, [app]: !prev[app] }));
  }, []);

  const toggleVerb = useCallback(
    (appLabel: string, model: string, verb: CrudVerb) => {
      const key = `${appLabel}.${model}`;
      setGrants((prev) => {
        const existing = prev[key] || {
          app_label: appLabel,
          model,
          can_view: false,
          can_add: false,
          can_change: false,
          can_delete: false,
        };
        const field = `can_${verb}` as const;
        return { ...prev, [key]: { ...existing, [field]: !existing[field] } };
      });
    },
    [setGrants],
  );

  const grantsList = useMemo(
    () =>
      Object.values(grants).filter(
        (g) => g.can_view || g.can_add || g.can_change || g.can_delete,
      ),
    [grants],
  );

  return {
    searchTerm,
    setSearchTerm,
    expandedApps,
    toggleApp,
    showSystemApps,
    setShowSystemApps,
    showEventModels,
    setShowEventModels,
    visibleApps,
    toggleVerb,
    grantsList,
  };
}
